import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import Icon from "../ui/Icon";

export const Sidebar = ({ isCollapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t } = useTranslation(['navigation', 'common']);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobile && isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, isMobileMenuOpen]);

  const menuItems = [
    {
      titleKey: "menu.dashboard",
      icon: "fas fa-tachometer-alt",
      path: "/dashboard",
      badge: null,
    },
    {
      titleKey: "menu.clubs",
      icon: "fas fa-users",
      path: "/clubs",
      badge: null,
    },
    {
      titleKey: "menu.events",
      icon: "fas fa-calendar-alt",
      path: "/events",
      badge: null,
    },
    {
      titleKey: "menu.users",
      icon: "fas fa-user-friends",
      path: "/users",
      badge: null,
    },
    {
      titleKey: "menu.universities",
      icon: "fas fa-university",
      path: "/universities",
      badge: null,
    },
    {
      titleKey: "menu.campuses",
      icon: "fas fa-building",
      path: "/campuses",
      badge: null,
    },
    {
      titleKey: "menu.faculties",
      icon: "fas fa-graduation-cap",
      path: "/faculties",
      badge: null,
    },
    {
      titleKey: "menu.departments",
      icon: "fas fa-sitemap",
      path: "/departments",
      badge: null,
    },
    {
      titleKey: "menu.countries",
      icon: "fas fa-globe",
      path: "/countries",
      badge: null,
    },
    {
      titleKey: "menu.cities",
      icon: "fas fa-city",
      path: "/cities",
      badge: null,
    },
    {
      titleKey: "menu.categories",
      icon: "fas fa-tags",
      path: "/categories",
      badge: null,
    },
    {
      titleKey: "menu.appConfig",
      icon: "fas fa-cog",
      path: "/app-config",
      badge: null,
    },
  ];

  const bottomMenuItems = [
    {
      titleKey: "menu.profile",
      icon: "fas fa-user",
      path: "/profile",
      badge: null,
    },
    {
      titleKey: "menu.logout",
      icon: "fas fa-sign-out-alt",
      path: "/logout",
      badge: null,
    },
  ];

  const isActiveRoute = (path) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const handleLogout = (e) => {
    e.preventDefault();
    logout();
    navigate("/login");
  };

  const showLabels = !isCollapsed || (isMobile && isMobileMenuOpen);

  const MenuItem = ({ item, isBottom = false }) => {
    const title = t(`navigation:${item.titleKey}`);
    return (
      <li className="nav-item">
        {item.path === "/logout" ? (
          <a
            href="/login"
            onClick={(e) => {
              handleLogout(e);
              isMobile && setIsMobileMenuOpen(false);
            }}
            className="nav-link-modern d-flex align-items-center"
            title={!showLabels ? title : ""}
          >
            <div className="nav-icon">
              <Icon name={item.icon.replace("fas fa-", "")} size="md" />
            </div>
            {showLabels && (
              <div className="nav-content">
                <span className="nav-title">{title}</span>
                {item.badge && (
                  <span className={`nav-badge bg-${item.badge.color}`}>
                    {t(`navigation:${item.badge.textKey}`)}
                  </span>
                )}
              </div>
            )}
          </a>
        ) : (
          <Link
            to={item.path}
            className={`nav-link-modern d-flex align-items-center ${
              isActiveRoute(item.path) ? "active" : ""
            }`}
            title={!showLabels ? title : ""}
            onClick={() => isMobile && setIsMobileMenuOpen(false)}
          >
            <div className="nav-icon">
              <Icon name={item.icon.replace("fas fa-", "")} size="md" />
            </div>
            {showLabels && (
              <div className="nav-content">
                <span className="nav-title">{title}</span>
                {item.badge && (
                  <span className={`nav-badge bg-${item.badge.color}`}>
                    {t(`navigation:${item.badge.textKey}`)}
                  </span>
                )}
              </div>
            )}
          </Link>
        )}
      </li>
    );
  };

  return (
    <>
      {/* Mobile Menu Toggle Button */}
      {isMobile && (
        <button
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <Icon name={isMobileMenuOpen ? "times" : "bars"} size="md" />
        </button>
      )}

      <div
        className={`sidebar-modern ${
          isCollapsed ? "sidebar-collapsed" : "sidebar-expanded"
        } ${
          isMobile ? (isMobileMenuOpen ? "mobile-open" : "mobile-closed") : ""
        }`}
      >
        {/* Sidebar Header */}
        <div className="sidebar-header-modern">
          {showLabels && (
            <div className="brand-section">
              <div className="brand-icon">
                <Icon name="graduation-cap" size="lg" />
              </div>
              <div className="brand-info">
                <h6 className="brand-title">{t('navigation:branding.appName')}</h6>
                <small className="brand-subtitle">{t('navigation:branding.adminPanel')}</small>
              </div>
            </div>
          )}

          <button
            className="sidebar-toggle"
            onClick={onToggle}
            title={t(isCollapsed ? 'navigation:tooltips.expandSidebar' : 'navigation:tooltips.collapseSidebar')}
          >
            <Icon
              name={isCollapsed ? "chevron-right" : "chevron-left"}
              size="md"
            />
          </button>
        </div>

        {/* Sidebar Body */}
        <div className="sidebar-body-modern">
          {/* Main Navigation */}
          <nav className="main-nav">
            {showLabels && <div className="nav-section-title">{t('navigation:sections.mainMenu')}</div>}
            <ul className="nav-list">
              {menuItems.map((item, index) => (
                <MenuItem key={index} item={item} />
              ))}
            </ul>
          </nav>

          {/* University Manager Demo Section */}
          <div className="um-demo-section">
            {showLabels && <div className="nav-section-title">{t('navigation:sections.demoMode')}</div>}
            <ul className="nav-list">
              <li className="nav-item">
                <Link
                  to="/university-manager"
                  className={`nav-link-modern d-flex align-items-center um-demo-link ${
                    isActiveRoute("/university-manager") ? "active" : ""
                  }`}
                  title={!showLabels ? t('navigation:universityManager.title') : ""}
                  onClick={() => isMobile && setIsMobileMenuOpen(false)}
                >
                  <div className="nav-icon">
                    <Icon name="university" size="md" />
                  </div>
                  {showLabels && (
                    <div className="nav-content">
                      <span className="nav-title">{t('navigation:universityManager.title')}</span>
                      <span className="nav-badge bg-warning text-dark">{t('navigation:badges.demo')}</span>
                    </div>
                  )}
                </Link>
              </li>
            </ul>
          </div>

          {/* Bottom Navigation */}
          <nav className="bottom-nav">
            {showLabels && <div className="nav-section-title">{t('navigation:sections.settings')}</div>}
            <ul className="nav-list">
              {bottomMenuItems.map((item, index) => (
                <MenuItem key={index} item={item} isBottom={true} />
              ))}
            </ul>
          </nav>
        </div>
      </div>

      {/* Mobile Overlay */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </>
  );
};
